import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "./index.css";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { bootstrapAuth, getAccessToken } from "./services/authSession";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const getActiveClientId = () => {
  const explicit = localStorage.getItem("active_client_id");
  if (explicit) {
    return explicit;
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.client_id || "";
  } catch {
    return "";
  }
};

const shouldAttachAuth = (rawUrl) => {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin === window.location.origin && url.pathname.startsWith("/api")) {
      return true;
    }
    return !!(BACKEND_URL && url.href.startsWith(BACKEND_URL));
  } catch {
    return false;
  }
};

// Every API call goes through one of these two interceptors. They ask
// services/authSession for the token, which is the app JWT (password mode)
// or a silently-refreshed Entra access token (Microsoft sign-in).
const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const requestUrl = typeof input === "string" ? input : input.url;
  if (!shouldAttachAuth(requestUrl)) {
    return originalFetch(input, init);
  }

  const token = await getAccessToken();
  const clientId = getActiveClientId();
  const headers = new Headers((input instanceof Request ? input.headers : undefined) || {});
  const initHeaders = new Headers(init.headers || {});
  initHeaders.forEach((value, key) => headers.set(key, value));

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (clientId && !headers.has("X-Client-Id")) {
    headers.set("X-Client-Id", clientId);
  }

  const nextInit = { ...init, headers };
  if (input instanceof Request) {
    return originalFetch(new Request(input, nextInit));
  }
  return originalFetch(input, nextInit);
};

axios.interceptors.request.use(async (config) => {
  const requestUrl = config.url || "";
  if (!shouldAttachAuth(requestUrl)) {
    return config;
  }
  const token = await getAccessToken();
  const clientId = getActiveClientId();
  const headers = config.headers || {};
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (clientId && !headers["X-Client-Id"]) {
    headers["X-Client-Id"] = clientId;
  }
  config.headers = headers;
  return config;
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Resolve the sign-in mode (and finish any Microsoft redirect) before the
// first render so components can rely on getStoredToken() synchronously.
bootstrapAuth()
  .catch((err) => console.error("[auth] bootstrap failed", err))
  .finally(() => {
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>,
    );
  });
